import { Flex } from "../Flex";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import { useFocusRing } from "../util/useFocusRing";

import React, { useMemo } from "react";
import { Platform, Pressable, StyleSheet, type ViewStyle } from "react-native";

export interface AlertAction {
  label: string;
  onPress: () => void;
  /** Disables the action and prevents onPress from firing. */
  disabled?: boolean;
}

export type AlertActionLayout = "inline" | "stacked";

interface AlertActionsProps {
  actions: AlertAction[];
  layout: AlertActionLayout;
}

function AlertActionButton({ label, onPress, disabled }: AlertAction) {
  const theme = useTheme();
  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityState={{ disabled }}
      onFocus={focusProps.onFocus}
      onBlur={focusProps.onBlur}
      aria-label={label}
      accessibilityRole="button"
      style={[
        { alignItems: "center", justifyContent: "center" },
        Platform.OS === "web" &&
          isFocusVisible &&
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
          ({
            outlineStyle: "solid",
            outlineWidth: 2,
            outlineColor: theme.colors.utilityFocus,
            outlineOffset: 2,
          } as ViewStyle),
        disabled && { opacity: 0.4 },
      ]}
    >
      <Text level={3} sx={{ fontWeight: "bold", color: theme.colors.foregroundActionStrong }}>
        {label}
      </Text>
    </Pressable>
  );
}

export function AlertActions({ actions, layout }: AlertActionsProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        inlineContainer: {
          flexDirection: "row",
          gap: theme.spacing.s3,
          justifyContent: "center",
          paddingRight: theme.spacing.s2,
        },
        stackedContainer: {
          alignItems: "flex-start",
          paddingTop: theme.spacing.s1,
          gap: theme.spacing.s2,
        },
      }),
    [theme],
  );
  return (
    <Flex sx={layout === "inline" ? styles.inlineContainer : styles.stackedContainer}>
      {actions.map(({ label, onPress, disabled }) => (
        <AlertActionButton
          key={`${label}-action`}
          label={label}
          onPress={onPress}
          disabled={disabled}
        />
      ))}
    </Flex>
  );
}
